<template>
  <v-menu
    v-model="open"
    offset-y
    left
    :close-on-content-click="false"
    min-width="380"
    max-width="380"
  >
    <template v-slot:activator="{ on: onMenu }">
      <v-tooltip bottom>
        <template v-slot:activator="{ on: onTooltip }">
          <button
            v-on="{ ...onMenu, ...onTooltip }"
            type="button"
            class="notification-dropdown-activator default-icon-btn"
            :class="{ active: open }"
            data-testid="notification-dropdown-button"
            aria-label="알림"
          >
            <img src="/assets/menu_icon/notification_off.svg" alt="" width="20" height="20">
            <span
              v-if="notificationState.totalUnread > 0"
              class="notification-navbar-badge"
              data-testid="notification-unread-badge"
            >{{ countText(notificationState.totalUnread) }}</span>
          </button>
        </template>
        <span>알림</span>
      </v-tooltip>
    </template>

    <v-card class="notification-dropdown-card" data-testid="notification-dropdown">
      <header class="notification-dropdown-header">
        <div>
          <strong>새 알림</strong>
          <span>읽지 않은 알림 {{ notificationState.totalUnread }}건</span>
        </div>
        <v-progress-circular v-if="notificationState.loading" indeterminate color="#687EFF" :size="20" :width="2" />
      </header>

      <div v-if="partialFailure" class="notification-dropdown-warning">일부 알림을 불러오지 못했습니다.</div>
      <div v-if="!notificationState.loading && notificationState.previewItems.length === 0" class="notification-dropdown-empty">
        읽지 않은 알림이 없습니다.
      </div>
      <button
        v-for="item in notificationState.previewItems"
        :key="item.key"
        type="button"
        class="notification-preview-item"
        data-testid="notification-preview-item"
        :disabled="readingKey === item.key"
        @click="openNotification(item)"
      >
        <span class="notification-preview-dot"></span>
        <span class="notification-preview-content">
          <span><strong>{{ item.title }}</strong><small>{{ sourceLabel(item.source) }}</small></span>
          <span>{{ item.message || '-' }}</span>
          <time>{{ formatDate(item.createdAt) }}</time>
        </span>
      </button>

      <button type="button" class="notification-dropdown-footer" @click="goAllNotifications">
        모든 알림 보기
        <v-icon size="18">mdi-chevron-right</v-icon>
      </button>
    </v-card>
  </v-menu>
</template>

<script>
import { notificationCenterStore } from '@/features/notifications/notification-center-store'
import { notificationTabLocation } from '@/features/notifications/notification-center-domain.mjs'

const SOURCE_LABELS = { security: '보안', work: '할일', cs: 'CS', system: '시스템' }

export default {
  name: 'NotificationDropdown',
  data() {
    return {
      open: false,
      readingKey: null,
      notificationState: notificationCenterStore.state,
    }
  },
  computed: {
    partialFailure() {
      return Object.values(this.notificationState.errors).some(Boolean)
    },
  },
  watch: {
    open(value) {
      if (value) this.refresh()
    },
  },
  methods: {
    countText(count) {
      return count > 99 ? '99+' : String(count)
    },
    sourceLabel(source) {
      return SOURCE_LABELS[source] || '알림'
    },
    formatDate(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '-'
      return date.toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
    },
    refresh() {
      return notificationCenterStore.refreshSummary({
        force: true,
        userId: this.$store.state.user_info?.pk || this.$store.state.user_info?.id,
        permission: this.$store.state.user_info?.organization_permission,
      }).catch(() => {})
    },
    async openNotification(item) {
      if (this.readingKey) return
      this.readingKey = item.key
      try {
        await notificationCenterStore.markRead(item)
        notificationCenterStore.refreshSummary({ force: true }).catch(() => {})
        let target
        try {
          target = await notificationCenterStore.resolveTarget(item)
        } catch (error) {
          this.$store.commit('swalToast', {
            title: error?.message || '알림의 상세 화면을 찾을 수 없습니다.',
            type: 'warning',
          })
          target = notificationTabLocation(item.source)
        }
        this.open = false
        if (target) await this.$router.push(target).catch(() => {})
      } catch (error) {
        this.open = false
        this.$store.commit('swalToast', { title: '알림을 처리하지 못했습니다. 다시 시도해 주세요.', type: 'warning' })
      } finally {
        this.readingKey = null
      }
    },
    goAllNotifications() {
      this.open = false
      this.$router.push({ name: 'NotificationCenter' }).catch(() => {})
    },
  },
}
</script>

<style scoped>
.notification-dropdown-activator {
  position: relative;
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
}

.notification-dropdown-activator.active {
  background: #0D0E170F !important;
}

.notification-navbar-badge {
  position: absolute;
  top: 4px;
  right: 4px;
  min-width: 12px;
  height: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 3.5px;
  border-radius: 999px;
  background: #ff3b30;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 12px;
}

.notification-dropdown-card {
  overflow: hidden;
  border: 1px solid #e8eaf2;
  border-radius: 12px !important;
}

.notification-dropdown-header {
  height: 66px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 18px;
  border-bottom: 1px solid #eff0f6;
}

.notification-dropdown-header strong,
.notification-dropdown-header span {
  display: block;
}

.notification-dropdown-header strong {
  color: #0d0e17;
  font-size: 16px;
}

.notification-dropdown-header span {
  margin-top: 3px;
  color: #8a8da8;
  font-size: 12px;
}

.notification-dropdown-warning {
  padding: 8px 16px;
  background: #fff8e8;
  color: #9a6200;
  font-size: 12px;
}

.notification-dropdown-empty {
  padding: 42px 18px;
  color: #8a8da8;
  text-align: center;
  font-size: 13px;
}

.notification-preview-item {
  width: 100%;
  min-height: 82px;
  display: flex;
  gap: 10px;
  padding: 13px 16px;
  border: 0;
  border-bottom: 1px solid #eff0f6;
  background: #fff;
  text-align: left;
  cursor: pointer;
}

.notification-preview-item:hover {
  background: #f8f9fb;
}

.notification-preview-item:disabled {
  opacity: 0.55;
  cursor: wait;
}

.notification-preview-dot {
  width: 8px;
  height: 8px;
  flex: 0 0 auto;
  margin-top: 5px;
  border-radius: 50%;
  background: #ff3b30;
}

.notification-preview-content {
  min-width: 0;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
}

.notification-preview-content > span:first-child {
  display: flex;
  align-items: center;
  gap: 7px;
}

.notification-preview-content strong {
  overflow: hidden;
  color: #0d0e17;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notification-preview-content small {
  flex: 0 0 auto;
  padding: 2px 6px;
  border-radius: 999px;
  background: #eff0f6;
  color: #687eff;
  font-size: 10px;
}

.notification-preview-content > span:nth-child(2) {
  overflow: hidden;
  color: #6b6e83;
  font-size: 12px;
  line-height: 17px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notification-preview-content time {
  color: #abaec4;
  font-size: 11px;
}

.notification-dropdown-footer {
  width: 100%;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  border: 0;
  background: #fff;
  color: #687eff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
</style>
